import { useEffect } from "react";
import {
  MiInputSelectBox,
  MiInputTextbox,
  MiModal,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { useComponentState, useEdit } from "@miplatform/hooks";
import { mdiAccountLockOpen } from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { useManagerApprovalModalViewModel } from "./useManagerApprovalModalViewModel";

const INITIAL_EDIT = {
  reason: "",
  userPin: "",
  managerId: null,
};

export function ManagerApprovalModal({
  closeModal = () => null,
  open = false,
  requestedPermission,
  execute = () => null,
  modalDescription = "Provide a reason for this override. A manager must enter their PIN below to approve and continue.",
  confirmBtnLabel = "Approve Override",
}) {
  const theme = useTheme();
  const edit = useEdit(INITIAL_EDIT);

  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const { reasons, getManagerWithPermissionToOverwrite, managers } =
    useManagerApprovalModalViewModel();

  useEffect(() => {
    if (open) {
      getManagerWithPermissionToOverwrite(requestedPermission);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestedPermission]);

  const closeAction = () => {
    closeModal();
    edit.reset();
  };

  const approveAction = async () => {
    stateManager.run(async () => {
      await execute({
        selectedUserId: edit.getValue("managerId"),
        userPin: edit.getValue("userPin"),
        reason: edit.getValue("reason"),
      });
      edit.reset();
    });
  };

  return (
    <MiModal
      open={open}
      closeModal={closeAction}
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          onClick: closeAction,
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
        },
        {
          name: confirmBtnLabel,
          onClick: approveAction,
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          disabled:
            !edit.allFilled("managerId", "userPin", "reason") || isLoading,
        },
      ]}
      description={modalDescription}
      width={448}
      title="Manager Approval Required"
      titleIcon={mdiAccountLockOpen}
      leftIconColor={theme.palette.deepPurple.dark}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <MiInputSelectBox
          label="Manager"
          options={managers}
          disableNone
          required
          handleChange={(e) => {
            edit.update({ managerId: e.target.value });
          }}
          value={edit.getValue("managerId")}
        />
        <MiInputTextbox
          size="small"
          type="password"
          required
          autoComplete="off"
          label="Manager PIN"
          value={edit.getValue("userPin")}
          handleChange={(e) => {
            edit.update({ userPin: e.target.value });
          }}
        />
        {reasons.length > 0 && (
          <MiInputSelectBox
            labelText="Reason"
            menuItems={reasons}
            handleChange={(e) => {
              edit.update({ reason: e.target.value });
            }}
            value={edit.getValue("reason")}
            size="small"
            disableNone
          />
        )}
        {reasons.length === 0 && (
          <MiInputTextbox
            required
            size="small"
            label="Reason"
            type="text"
            multiline
            rows={1}
            value={edit.getValue("reason")}
            handleChange={(e) => {
              edit.update({ reason: e.target.value });
            }}
          />
        )}
      </Box>
    </MiModal>
  );
}
